import { generateKeyBetween } from "fractional-indexing";
import { Viewer } from "cesium";
import { DeferredValue, isDeepEqual } from "lib/actions/util";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { FilterNode, Search } from "lib/cesium/search";
import {
  ObservableProperty,
  DerivedObservableProperty,
} from "./ObservableProperty";
import TilesetStylingPlugin, {
  StyleRule,
  TilesetStylePriority,
} from "./TilesetStylingPlugin";
import {
  SerializableToolInterface,
  compareToolState,
} from "./SerializableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";

export enum SearchRuleRenderMode {
  none = "none",
  colored = "colored",
  isolated = "isolated",
  excluded = "excluded",
}

export type SearchRuleStyle =
  | { type: "colored"; color: string }
  | { type: "isolated" }
  | { type: "excluded" };

export type Facets = { [property: string]: (string | number)[] };

/**
 * Placeholder for the legacy `nameGenerated` field the server still requires
 * on each persisted rule. The UI no longer generates or displays this value;
 * it's stubbed here purely for backwards compatibility until the field is
 * removed from the server model.
 */
export const DEPRECATED_NAME_GENERATED_PLACEHOLDER = "Rule";

export type SearchRuleEntry = {
  id: string;
  enabled: boolean;
  /** Fractional index for ordering. */
  position: string;
  style: SearchRuleStyle;
  facets: Facets;
  namePreferred?: string;
  /**
   * @deprecated Retained only so persisted rules satisfy the server's
   * required field; no longer generated or shown by the UI.
   */
  nameGenerated: string;
};

export type SearchToolSnapshot = {
  version: 1;
  searches: SearchRuleEntry[];
};

export function isSearchStateApplied(
  state: SearchToolSnapshot | undefined,
): boolean {
  return (
    state?.searches?.some(
      (s) => s.enabled && Object.keys(s.facets ?? {}).length > 0,
    ) ?? false
  );
}

export type PresetSearchRuleStyle = {
  style: SearchRuleStyle;
  swatchColor?: string;
  label: string;
};

export const presetSearchRuleStyles = {
  blue: {
    style: { type: SearchRuleRenderMode.colored, color: "#0000FF" },
    swatchColor: "#6366F1",
    label: "Blue",
  },
  red: {
    style: { type: SearchRuleRenderMode.colored, color: "red" },
    swatchColor: "#EF4444",
    label: "Red",
  },
  yellow: {
    style: { type: SearchRuleRenderMode.colored, color: "#FCBF49" },
    swatchColor: "#F59E0B",
    label: "Yellow",
  },
  isolate: {
    style: { type: SearchRuleRenderMode.isolated },
    label: "Isolated",
  },
  exclude: {
    style: { type: SearchRuleRenderMode.excluded },
    label: "Excluded",
  },
} as const satisfies Record<string, PresetSearchRuleStyle>;

export type PresetSearchRuleStyleKey = keyof typeof presetSearchRuleStyles;

export function findPresetSearchRuleStyle(
  style: SearchRuleStyle,
): PresetSearchRuleStyleKey | undefined {
  return (
    Object.keys(presetSearchRuleStyles) as PresetSearchRuleStyleKey[]
  ).find((key) => isDeepEqual(presetSearchRuleStyles[key].style, style));
}

/**
 * Resolves the full preset for a persisted `SearchRuleStyle`, falling
 * back to custom where no preset is found.
 */
export function resolvePresetSearchRuleStyle(
  style: SearchRuleStyle,
): PresetSearchRuleStyle {
  const key = findPresetSearchRuleStyle(style);
  if (key) return presetSearchRuleStyles[key];
  return {
    style,
    swatchColor:
      style.type === SearchRuleRenderMode.colored ? style.color : undefined,
    label: "Custom",
  };
}

export default class AssetsPropertiesSearchToolPlugin
  implements
    AttachablePluginInterface,
    SerializableToolInterface<SearchToolSnapshot>,
    BackgroundableToolInterface
{
  private readonly _viewer = new DeferredValue<Viewer>();
  /** Tracks id and last applied priority so stale rules can be removed. */
  private readonly _activePriorities = new Map<string, number>();
  private _isSuppressed = false;
  private readonly _priorityBase = TilesetStylePriority.ElementSearch + 1;

  private readonly _rules = new ObservableProperty<SearchRuleEntry[]>([]);
  readonly isApplied = new DerivedObservableProperty(
    () => isSearchStateApplied(this.capture()),
    [this._rules],
  );

  constructor(private readonly _tilesetStylingPlugin: TilesetStylingPlugin) {}

  get rules() {
    return this._rules.asReadonly();
  }

  setViewer(viewer: Viewer): void {
    this._viewer.set(viewer);
  }

  detachViewer(): void {
    // Clear the priority map so it reflects the true state after TilesetStylingPlugin
    // wipes its own _rules array on detach (plugins detach in registration order).
    this._activePriorities.clear();
    this._viewer.clear();
  }

  addRule(id: string): string {
    const current = this._rules.getSnapshot();
    const rule: SearchRuleEntry = {
      id,
      namePreferred: null,
      nameGenerated: DEPRECATED_NAME_GENERATED_PLACEHOLDER,
      position: generateKeyBetween(
        current.length > 0 ? current.at(-1).position : null,
        null,
      ),
      style: presetSearchRuleStyles.red.style,
      facets: {},
      enabled: true,
    };
    const next = [...current, rule];
    this._rules.set(next);
    this.syncRulesAndApply(next);
    return id;
  }

  removeRule(id: string) {
    const current = this._rules.getSnapshot();
    const next = current.filter((s) => s.id !== id);
    this._rules.set(next);
    this.syncRulesAndApply(next);
  }

  updateRule(
    id: string,
    updates: Partial<
      Pick<SearchRuleEntry, "facets" | "style" | "namePreferred" | "position">
    >,
  ) {
    const current = this._rules.getSnapshot();
    const next = current.map((s) => (s.id !== id ? s : { ...s, ...updates }));
    this._rules.set(next);
    this.syncRulesAndApply(next);
  }

  toggleRule(id: string) {
    const current = this._rules.getSnapshot();
    const next = current.map((s) =>
      s.id !== id ? s : { ...s, enabled: !s.enabled },
    );
    this._rules.set(next);
    this.syncRulesAndApply(next);
  }

  setRules(rules: SearchRuleEntry[]) {
    this._rules.set(rules);
    this.syncRulesAndApply(rules);
  }

  clear(): void {
    const current = this._rules.getSnapshot();
    const next = current.map((s) => ({ ...s, enabled: false }));
    this._rules.set(next);
    this.syncRulesAndApply(next);
  }

  setSuppressed(isSuppressed: boolean): void {
    if (this._isSuppressed === isSuppressed) return;
    this._isSuppressed = isSuppressed;
    this.syncRulesAndApply(this._rules.getSnapshot());
  }

  capture(): SearchToolSnapshot {
    return { version: 1, searches: this._rules.getSnapshot() };
  }

  restore(state: SearchToolSnapshot | undefined): void {
    if (!state?.searches) return;
    this.setRules(state.searches);
  }

  isStateApplied(state: SearchToolSnapshot | undefined): boolean {
    return isSearchStateApplied(state);
  }

  isMatchingState(state: SearchToolSnapshot | undefined): boolean {
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  private syncRulesAndApply(rules: SearchRuleEntry[]) {
    this.syncRules(rules);
    void this._tilesetStylingPlugin.applyRules();
  }

  private syncRules(rules: SearchRuleEntry[]) {
    for (const priority of this._activePriorities.values()) {
      this._tilesetStylingPlugin.removeRule({ priority, conditionStyle: {} });
    }
    this._activePriorities.clear();

    const activeTopDown = (this._isSuppressed ? [] : rules)
      .filter((s) => s.enabled && Object.keys(s.facets).length > 0)
      .toSorted((a, b) => (a.position < b.position ? -1 : 1));

    activeTopDown.forEach((entry, rank) => {
      const priorityOffset = rank / 256; //allows many slots near the base ElementSearch priority
      const priority = this._priorityBase + priorityOffset;
      this._activePriorities.set(entry.id, priority);

      const search = Search.fromFacets(entry.facets);
      if (search) {
        this._tilesetStylingPlugin.setRule(
          this.createRule(search, entry.style, priority),
        );
      }
    });
  }

  private createRule(
    search: FilterNode,
    style: SearchRuleStyle,
    priority: number,
  ): StyleRule {
    const condition = Search.toCesium3dTileStyleCondition(search);
    return {
      priority,
      conditionStyle: {
        condition,
        ...getStyleForRuleStyle(style),
      },
    };
  }
}

export function getStyleForRuleStyle(style: SearchRuleStyle) {
  switch (style.type) {
    case SearchRuleRenderMode.colored:
      return { color: style.color, opacity: 0.5 };
    case SearchRuleRenderMode.isolated:
      return { isolated: true };
    case SearchRuleRenderMode.excluded:
      return { hidden: true };
  }
}
